import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Bell, NotebookText, RefreshCw, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import {
  ASK_COMPANION, plainReply, timeAgo, useAssistantStatus, useDailyDigest, useMonitorStatus, useNotificationSummary,
  useOpenIncidents, type CompanionAsk,
} from '@/lib/assistant';
import {
  DEFAULT_DOCK_POSITION, bubblePlacement, buildCompanionBrief, buildCompanionReport, companionBriefKey,
  companionDataIsCurrent, parseCompanionPreferences,
} from '@/lib/companion';
import type { CompanionPreferences, DockSide, ReportTone } from '@/lib/companion';
import { useCompanionDevices, useSystemOverview } from '@/lib/useSystemOverview';
import { Button } from '@/components/ui/button';
import { SettingSwitch } from '@/components/ui/setting-switch';
import { openNotifications } from '@/components/notifications/NotificationBell';
import '@/components/notifications/notifications.css';
import { CompanionChat } from './CompanionChat';
import { CompanionDock } from './CompanionDock';
import { ReportActions, ReportBody, ReportSkeleton } from './CompanionReport';
import type { ReportDevicesState } from './CompanionReport';
import { DOCK_HEIGHT, useCompanionDock } from './useCompanionDock';
import { XiaoShao } from './XiaoShao';

const GREETINGS = ['你好，我在這裡。', '想先看哪一項？', '慢慢來，一項一項處理就好。'];
type PanelTab = 'summary' | 'chat';
// 和 AppShell 底部導覽的 md:hidden 同一個斷點
const PHONE_QUERY = '(max-width: 767px)';
// 1024 以上內容區夠寬，登入報告才貼著小哨浮在旁邊
const INLINE_REPORT_QUERY = '(max-width: 1023px)';

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const media = matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);
  return matches;
}

/* 手機上小哨不浮在內容上：收合的小哨佔 76×93px，會蓋住卡片右側的按鈕
   （10-30 實測 375 寬第一張裝置卡的「更多操作」被擋），捲動也只是換一張被擋。
   所以手機改用 portal 放進 AppShell 底部導覽的 navSlot，登入報告放在主內容最上方。
   平板與電腦版是實底卡片，可以拖到左右兩側（2026-10-31）。1024 以上登入報告貼在卡片旁邊；
   平板扣掉側欄後，400px 寬的泡泡會蓋住一半左右的總覽，所以和手機一樣放在主內容最上方。 */
export function SystemCompanion({ navSlot = null, onNavOpen }: {
  navSlot?: HTMLElement | null; onNavOpen?: () => void;
}) {
  const { user } = useAuth();
  const overview = useSystemOverview();
  const notices = useNotificationSummary();
  const assistant = useAssistantStatus();
  const { pathname } = useLocation();
  // /auth/me 不回 id，偏好、位置與「本分頁已顯示過」的記號都用帳號名稱當鍵
  const account = user?.username ?? '';
  const preferenceKey = `edgeai:companion:v1:${user?.username}`;
  const [preferences, setPreferences] = useState(() => {
    try { return parseCompanionPreferences(localStorage.getItem(preferenceKey)); }
    catch { return parseCompanionPreferences(null); }
  });
  const [welcome, setWelcome] = useState(() => {
    try { return preferences.loginBrief && Boolean(user) && sessionStorage.getItem(companionBriefKey(user!.username)) !== 'shown'; }
    catch { return preferences.loginBrief; }
  });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PanelTab>('summary');
  // 裝置詳情、通知中心丟來的提問，等對話分頁掛上後由 CompanionChat 送出
  const [pendingAsk, setPendingAsk] = useState<CompanionAsk | null>(null);
  // wave：小哨招手（滑過、聚焦、待處理變多）；greeted：使用者主動打招呼的次數，決定回哪一句
  const [wave, setWave] = useState(0);
  const [greeted, setGreeted] = useState(0);
  const [nudge, setNudge] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [now, setNow] = useState(Date.now);
  const phone = useMediaQuery(PHONE_QUERY);
  const inlineReport = useMediaQuery(INLINE_REPORT_QUERY);
  const dock = useCompanionDock(account, !phone);
  // 面板開著時按「換到左側」只移動小哨，面板留在原處，不會從手底下跑掉
  const [panelSide, setPanelSide] = useState<DockSide>(dock.side);
  // 提問事件的監聽只註冊一次，用 ref 讀目前的位置
  const dockSide = useRef(dock.side);
  dockSide.current = dock.side;
  const dockRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLElement>(null);
  // 手機上歡迎卡和導覽列各有一個入口，Radix 只記得最後掛上的那個，關閉時自己把焦點送回按下的入口
  const returnFocus = useRef<HTMLElement | null>(null);
  const previousPath = useRef(pathname);
  const lastAttention = useRef<number | null>(null);

  // 各裝置清單、異常與監控狀態只在報告看得到時才查
  const reportVisible = welcome || (open && tab === 'summary');
  const devices = useCompanionDevices(reportVisible);
  const incidents = useOpenIncidents({ enabled: reportVisible });
  const monitor = useMonitorStatus({ enabled: reportVisible });
  const digest = useDailyDigest({ enabled: open && tab === 'summary' });

  const current = companionDataIsCurrent(overview.dataUpdatedAt, now, online, overview.isError);
  const brief = overview.data && user ? buildCompanionBrief(overview.data, user.role) : null;
  const ready = current && brief !== null;
  const attention = ready ? brief.items.length : 0;
  const bubbleOpen = welcome && !inlineReport;

  useEffect(() => {
    if (!welcome || !user) return;
    try { sessionStorage.setItem(companionBriefKey(user.username), 'shown'); }
    catch { /* 本次掛載期間仍只顯示一次。 */ }
  }, [welcome, user]);

  useEffect(() => {
    if (previousPath.current !== pathname) setWelcome(false);
    previousPath.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const handler = (event: Event) => {
      const ask = (event as CustomEvent<CompanionAsk>).detail;
      if (!ask?.text) return;
      setPendingAsk(ask);
      setTab('chat');
      setWelcome(false);
      setPanelSide(dockSide.current);
      setOpen(true);
    };
    window.addEventListener(ASK_COMPANION, handler);
    return () => window.removeEventListener(ASK_COMPANION, handler);
  }, []);

  useEffect(() => {
    const update = () => { setOnline(navigator.onLine); setNow(Date.now()); };
    const interval = window.setInterval(() => setNow(Date.now()), 10_000);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  // 待處理的事變多時，小哨招手、卡片輕推一下；第一次載入不算
  useEffect(() => {
    if (!ready) return;
    const previous = lastAttention.current;
    lastAttention.current = attention;
    if (previous !== null && attention > previous) {
      setWave((value) => value + 1);
      setNudge((value) => value + 1);
    }
  }, [ready, attention]);

  // 報告泡泡不是對話框，焦點在頁面任何地方按 Esc 都能收起；其他對話框先處理掉的 Esc 不算
  useEffect(() => {
    if (!bubbleOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const active = document.activeElement;
      const inside = Boolean(active && (bubbleRef.current?.contains(active) || active === dockRef.current));
      setWelcome(false);
      if (inside) requestAnimationFrame(() => dockRef.current?.focus());
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [bubbleOpen]);

  function changePreference(name: keyof CompanionPreferences, checked: boolean) {
    const next = { ...preferences, [name]: checked };
    setPreferences(next);
    try { localStorage.setItem(preferenceKey, JSON.stringify(next)); }
    catch { /* 瀏覽器不允許保存時，這次仍可正常使用。 */ }
  }

  if (!user) return null;
  const devicesState: ReportDevicesState = devices.data ? 'ready' : devices.isError ? 'error' : 'loading';
  // 裝置與異常都查完才寫結論，報讀軟體不會先唸一次不完整的版本
  const settled = devicesState !== 'loading' && !incidents.isPending;
  const report = ready && overview.data ? buildCompanionReport({
    overview: overview.data, devices: devices.data, incidents: incidents.data, monitor: monitor.data,
    now, maxDevices: phone ? 3 : 5,
  }) : null;
  const title = !online ? '網路已中斷，暫時無法確認狀態'
    : overview.isError ? '暫時讀不到系統狀態'
      : overview.isPending ? '正在讀取系統狀態…'
        : !current ? '資料已超過一分鐘未更新' : brief?.title ?? '尚未取得系統狀態';
  const detail = ready ? (report && settled ? report.headline : brief.detail) : !online ? '連線恢復後，請重新整理摘要。'
    : overview.isPending ? '稍候就會顯示你有權限查看的資料。' : '請重新整理，再確認最新的裝置與檔案狀態。';
  const expression = !online || overview.isError || (ready && brief.needsAttention) ? 'notice'
    : !ready ? 'left' : undefined;
  const [dockStatus, dockTone]: [string, ReportTone] = !online ? ['網路中斷', 'warn']
    : overview.isError ? ['讀不到狀態', 'warn']
      : overview.isPending ? ['讀取中…', 'muted']
        : !ready ? ['資料未更新', 'muted']
          : attention > 0 ? [`${attention} 項待處理`, brief.needsAttention ? 'warn' : 'info']
            : overview.data?.devices.total === 0 ? ['尚無裝置', 'info'] : ['目前沒有異常', 'ok'];
  const firstName = user.display_name || user.username;
  const hour = new Date(now).getHours();
  const salutation = hour < 5 || hour >= 23 ? '夜深了' : hour < 11 ? '早安' : hour < 18 ? '午安' : '晚安';
  const unread = typeof notices.data?.unread === 'number' ? notices.data.unread : 0;
  const canChat = assistant.data?.ready === true;
  const action = ready ? { href: brief.href, label: brief.action } : null;
  // 假 API 或舊版後端對未知端點回 []，只認得有 body 的那種形狀
  const todayDigest = typeof digest.data?.digest?.body === 'string' ? digest.data.digest : null;
  const placement = bubblePlacement(dock.bottom, DOCK_HEIGHT, dock.viewport.height);
  const remember = (event: SyntheticEvent<HTMLElement>) => { returnFocus.current = event.currentTarget; };
  const greet = () => { setGreeted((value) => value + 1); setWave((value) => value + 1); };

  function changeOpen(next: boolean) {
    if (next) {
      setPanelSide(dock.side);
      // 面板和報告泡泡都貼著小哨，同時開會疊在一起；主內容最上方的報告卡片留著
      if (bubbleOpen) setWelcome(false);
    }
    setOpen(next);
  }

  function collapse() {
    setWelcome(false);
    requestAnimationFrame(() => dockRef.current?.focus());
  }

  function showNotifications() {
    setOpen(false);
    // 等小哨面板關好、焦點歸位後再開通知中心，兩個對話框才不會搶焦點
    window.setTimeout(() => openNotifications('inbox'), 0);
  }

  function askXiaoShao(event: SyntheticEvent<HTMLElement>) {
    remember(event);
    setTab('chat');
    changeOpen(true);
  }

  function refresh() {
    void overview.refetch();
    void devices.refetch();
    void incidents.refetch();
    void monitor.refetch();
  }

  const headline = <>
    <p className="companion-headline" aria-live="polite">{ready ? (report && settled ? report.headline : '正在整理各裝置的狀況…') : title}</p>
    {!ready && <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{detail}</p>}
    {unread > 0 && <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
      你不在的時候有 {unread > 99 ? '99+' : unread} 則通知還沒看。
    </p>}
  </>;
  // 讀不到或資料過期時只說原因，不放可能已經不對的連結
  const reportBlock = report ? <ReportBody report={report} devicesState={devicesState} onNavigate={() => setWelcome(false)} />
    : overview.isPending && online ? <ReportSkeleton /> : null;
  const actions = action || unread > 0 || canChat ? <ReportActions action={action} unread={unread} canChat={canChat}
    onNavigate={() => setWelcome(false)} onNotifications={() => openNotifications('inbox')} onAsk={askXiaoShao} /> : null;
  const collapseButton = <button type="button" onClick={collapse} aria-label="收合小哨摘要" title="收合小哨摘要" className="companion-icon-button">
    <X aria-hidden="true" size={17} />
  </button>;

  return <Dialog.Root open={open} onOpenChange={changeOpen}>
    {welcome && inlineReport && <section className="companion-welcome mb-5" aria-label="小哨的登入摘要">
      <div className="companion-welcome-head">
        <button type="button" className="companion-welcome-pet" aria-label="跟小哨打招呼" onClick={greet}>
          <XiaoShao expression={expression} motion={preferences.motion} paused={open} reaction={wave} />
        </button>
        <div className="min-w-0 flex-1 pt-3">
          <h2 className="text-[14px] font-semibold">{salutation}，{firstName}。</h2>
          {headline}
          {greeted > 0 && <p className="mt-1 text-xs text-muted-foreground" role="status">{GREETINGS[(greeted - 1) % GREETINGS.length]}</p>}
        </div>
        {collapseButton}
      </div>
      {reportBlock && <div className="companion-welcome-body">{reportBlock}</div>}
      {actions && <div className="companion-welcome-body">{actions}</div>}
    </section>}

    {bubbleOpen && <section ref={bubbleRef} className="companion-bubble" aria-label="小哨的登入摘要"
      data-side={dock.side} data-place={placement.place} data-motion={preferences.motion ? undefined : 'off'}
      style={{ '--dock-bottom': `${dock.bottom}px` } as CSSProperties}>
      <header className="companion-bubble-head">
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold">{salutation}，{firstName}。</h2>
          {headline}
        </div>
        {collapseButton}
      </header>
      {reportBlock && <div className="companion-bubble-body">{reportBlock}</div>}
      {actions && <footer className="companion-bubble-foot">{actions}</footer>}
    </section>}

    {!phone && <CompanionDock buttonRef={dockRef} label={`小哨系統摘要：${title}`} status={dockStatus} tone={dockTone}
      attention={attention} side={dock.side} bottom={dock.bottom} viewport={dock.viewport} motion={preferences.motion}
      nudge={nudge} onMove={dock.move} onDragStart={() => { if (bubbleOpen) setWelcome(false); }} onActivate={remember}
      onGreet={() => setWave((value) => value + 1)}>
      <XiaoShao expression={expression} motion={preferences.motion} paused={open} reaction={wave} />
    </CompanionDock>}

    {/* 導覽列那格常駐，歡迎卡出現時也在，底部導覽的格數才不會跳動 */}
    {phone && navSlot && createPortal(<Dialog.Trigger asChild>
      <button ref={dockRef} type="button" data-active={open}
        className="sidebar-item companion-nav-item !min-h-[64px] w-full min-w-0 flex-col justify-center gap-1 rounded-none !px-1 text-center"
        aria-label={`小哨系統摘要：${title}`}
        onClick={(event) => { remember(event); onNavOpen?.(); }} onFocus={() => setWave((value) => value + 1)}>
        <span className="companion-nav-pet">
          <XiaoShao compact expression={expression} motion={preferences.motion} paused={open} reaction={wave} />
          {attention > 0 && <span className="companion-nav-badge" aria-hidden="true">{attention > 9 ? '9+' : attention}</span>}
        </span>
        <span className="w-full truncate text-[11px] leading-tight">小哨</span>
      </button>
    </Dialog.Trigger>, navSlot)}

    <Dialog.Portal>
      <Dialog.Overlay className="companion-overlay" />
      <Dialog.Content className="companion-panel" data-side={panelSide} onCloseAutoFocus={(event) => {
        const target = returnFocus.current;
        if (target?.isConnected) { event.preventDefault(); target.focus(); }
      }}>
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <Dialog.Title className="text-[17px] font-bold">小哨</Dialog.Title>
            <Dialog.Description className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
              {tab === 'chat' ? '查詢只讀你有權限的資料；要動到資料或裝置，會先請你確認。' : '先看需要留意的事，再決定從哪裡開始。'}
            </Dialog.Description>
          </div>
          <Dialog.Close asChild><button type="button" className="companion-icon-button" aria-label="關閉小哨"><X aria-hidden="true" size={18} /></button></Dialog.Close>
        </header>

        <Tabs.Root value={tab} onValueChange={(value) => setTab(value as PanelTab)} className="flex min-h-0 flex-1 flex-col">
        <Tabs.List className="tab-list px-3" aria-label="小哨的功能">
          <Tabs.Trigger value="summary" className="tab-trigger">系統摘要</Tabs.Trigger>
          <Tabs.Trigger value="chat" className="tab-trigger">問小哨</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="summary" className="flex min-h-0 flex-1 flex-col focus-visible:outline-none">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="h-[88px] w-[88px] shrink-0"><XiaoShao expression={expression} motion={preferences.motion} paused reaction={wave} /></div>
            <div className="min-w-0">
              <p className="text-[14px] font-semibold" role="status">{title}</p>
              <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{detail}</p>
            </div>
          </div>

          {report && <div className="mt-3">
            <ReportBody report={report} devicesState={devicesState} onNavigate={() => setOpen(false)} />
          </div>}

          {todayDigest && <section className="companion-digest" aria-label="每日值班摘要">
            <h3 className="flex items-center gap-1.5 text-[13px] font-semibold">
              <NotebookText aria-hidden="true" size={14} className="shrink-0 text-muted-foreground" />
              {todayDigest.is_today ? '今天的值班摘要' : '昨天的值班摘要'}
            </h3>
            <p className="companion-digest-body">{plainReply(todayDigest.body)}</p>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {todayDigest.source === 'llm' ? '小哨用 AI 整理' : '依監控規則整理'}・{timeAgo(todayDigest.created_at)}產生
            </p>
          </section>}

          {unread > 0 && <button type="button" onClick={showNotifications}
            className="mt-3 flex w-full min-h-[44px] items-center gap-2 rounded-md border border-border px-3 text-left text-[12.5px] transition-colors hover:bg-secondary">
            <Bell aria-hidden="true" size={15} className="shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">通知中心有 {unread > 99 ? '99+' : unread} 則未讀</span>
            <ArrowRight aria-hidden="true" size={14} className="shrink-0 text-muted-foreground" />
          </button>}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <span className="text-[11px] text-muted-foreground">
              {overview.dataUpdatedAt ? <>上次取得 <time dateTime={new Date(overview.dataUpdatedAt).toISOString()}>{new Date(overview.dataUpdatedAt).toLocaleTimeString('zh-TW', { hour12: false })}</time></> : '尚未取得資料'}
            </span>
            <Button variant="secondary" size="sm" disabled={overview.isFetching || !online} onClick={refresh}>
              <RefreshCw aria-hidden="true" size={14} className={overview.isFetching ? 'animate-spin' : ''} />
              {overview.isFetching ? '更新中…' : '重新整理'}
            </Button>
          </div>

          <div className="mt-4 border-t border-border pt-3">
            <SettingSwitch label="播放表情互動" description="眨眼、張望與招手；配合裝置的減少動態設定。" checked={preferences.motion} onChange={(checked) => changePreference('motion', checked)} />
            <SettingSwitch label="登入後顯示摘要" description="登入後小哨會主動整理整體與各裝置狀況，每個分頁只出現一次。" checked={preferences.loginBrief} onChange={(checked) => changePreference('loginBrief', checked)} />
            {!phone && <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium">小哨位置</span>
                <span className="mt-1 block text-[11px] leading-relaxed text-muted-foreground">可以直接拖曳；選取小哨後，也能用方向鍵移動。</span>
              </span>
              <span className="flex shrink-0 gap-2">
                <Button variant="secondary" size="sm" onClick={() => dock.move({ side: dock.side === 'left' ? 'right' : 'left', bottom: dock.bottom })}>
                  {dock.side === 'left' ? '換到右側' : '換到左側'}
                </Button>
                <Button variant="ghost" size="sm" disabled={dock.isDefault} onClick={() => dock.move(DEFAULT_DOCK_POSITION)}>回到右下角</Button>
              </span>
            </div>}
          </div>
        </div>

        <footer className="border-t border-border px-5 py-3">
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 text-[12px] text-muted-foreground" role="status">{greeted ? GREETINGS[(greeted - 1) % GREETINGS.length] : '我是小哨，陪你留意現場。'}</p>
            <Button variant="ghost" size="sm" onClick={greet}>打個招呼</Button>
          </div>
        </footer>
        </Tabs.Content>

        <Tabs.Content value="chat" className="companion-chat focus-visible:outline-none">
          <CompanionChat user={user} onNavigate={() => setOpen(false)} ask={pendingAsk} onAskTaken={() => setPendingAsk(null)} />
        </Tabs.Content>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
